import type { TMessengerErpLayoutMode } from '@xometry/ui';
import { amplitudeLogger } from 'amplitude';
import { Tooltip } from 'antd';
import { AmplitudeAttributeKey, AmplitudeMessengerEvent } from 'collections/amplitudeEvents';
import React, { FC } from 'react';
import { ESide } from 'stores/SideOverlay/SideOverlayStore';

import {
  CrossIcon,
  HeaderControlsBlock,
  MessengerLayoutCollapseIcon,
  MessengerLayoutExpandIcon,
  SwapIcon,
} from './SideOverlayContainer.styled';

const MESSENGER_LAYOUT_OVERLAY_MODE_LABEL = 'Overlay Mode';
const MESSENGER_LAYOUT_SIDEBAR_MODE_LABEL = 'Sidebar Mode';

export interface IMessengerLayoutToggleConfig {
  currentMode: TMessengerErpLayoutMode;
  onToggle: () => void;
}

interface IProps {
  onClose: () => void;
  onToggleSide?: () => void;
  side: ESide;
  messengerLayoutToggle?: IMessengerLayoutToggleConfig;
}

export const HeaderControls: FC<IProps> = ({ onClose, onToggleSide, side, messengerLayoutToggle }) => {
  const handleCloseClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();

    amplitudeLogger({ event: AmplitudeMessengerEvent.MESSENGER_CLOSED });

    onClose();
  };

  const handleToggleSideClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();

    amplitudeLogger({
      event: AmplitudeMessengerEvent.MESSENGER_SIDE_SWAPPED,
      attrs: {
        [AmplitudeAttributeKey.SIDE]: side,
      },
    });

    onToggleSide?.();
  };

  const handleMessengerLayoutToggleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
    messengerLayoutToggle?.onToggle();
  };

  const handleMessengerLayoutToggleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      messengerLayoutToggle?.onToggle();
    }
  };

  const layoutTooltip =
    messengerLayoutToggle?.currentMode === 'compact'
      ? MESSENGER_LAYOUT_OVERLAY_MODE_LABEL
      : MESSENGER_LAYOUT_SIDEBAR_MODE_LABEL;

  const layoutIcon =
    messengerLayoutToggle?.currentMode === 'compact' ? (
      <MessengerLayoutExpandIcon
        aria-label={layoutTooltip}
        role="button"
        tabIndex={0}
        size="18px"
        onClick={handleMessengerLayoutToggleClick}
        onKeyDown={handleMessengerLayoutToggleKeyDown}
      />
    ) : (
      <MessengerLayoutCollapseIcon
        aria-label={layoutTooltip}
        role="button"
        tabIndex={0}
        size="18px"
        onClick={handleMessengerLayoutToggleClick}
        onKeyDown={handleMessengerLayoutToggleKeyDown}
      />
    );

  return (
    <HeaderControlsBlock>
      {messengerLayoutToggle ? (
        <Tooltip title={layoutTooltip} placement="left">
          <span>{layoutIcon}</span>
        </Tooltip>
      ) : null}

      {onToggleSide ? <SwapIcon onClick={handleToggleSideClick} size="18px" /> : null}
      <CrossIcon onClick={handleCloseClick} size="18px" />
    </HeaderControlsBlock>
  );
};
